import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2 } from 'lucide-react';
import {
  ALERT_EMAIL_ROUTES,
  OWNER_ALERT_DEDUPE_MAX_HOURS,
  OWNER_ALERT_MUTES_MAX,
  type AlertEmailRoute,
  type OwnerAlertEmailOverviewDto,
  type OwnerAlertEmailPolicy as Policy,
  type OwnerAlertType,
} from '@his/shared';
import { get, put, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Select, Spinner, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ALERT_TYPE_LABELS } from './OwnerAlertsFeed';
import { TotpField, isValidTotp } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

const QUERY_KEY = ['owner-alert-email-policy'];
const MUTE_DAYS = ['1', '7', '30', 'forever'] as const;
type MuteDays = (typeof MUTE_DAYS)[number];

interface TenantOption {
  id: string;
  slug: string;
  name: string;
}

export function setRule(policy: Policy, type: OwnerAlertType, patch: Partial<{ route: AlertEmailRoute; dedupeHours: number }>): Policy {
  return { ...policy, rules: policy.rules.map((rule) => (rule.type === type ? { ...rule, ...patch } : rule)) };
}

export function addMute(policy: Policy, tenantId: string, days: MuteDays, now: Date): Policy {
  const until = days === 'forever' ? null : new Date(now.getTime() + Number(days) * 24 * 60 * 60 * 1000).toISOString();
  return { ...policy, mutes: [...policy.mutes.filter((mute) => mute.tenantId !== tenantId), { tenantId, until }].slice(-OWNER_ALERT_MUTES_MAX) };
}

export function removeMute(policy: Policy, tenantId: string): Policy {
  return { ...policy, mutes: policy.mutes.filter((mute) => mute.tenantId !== tenantId) };
}

export function OwnerAlertEmailPolicy() {
  const { t } = useTranslation('admin');
  const toast = useToast();
  const queryClient = useQueryClient();
  const canSave = useOwnerCapability('alert_settings.update');
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [muteTenant, setMuteTenant] = useState('');
  const [muteDays, setMuteDays] = useState<MuteDays>('7');

  const overview = useQuery({ queryKey: QUERY_KEY, queryFn: () => get<OwnerAlertEmailOverviewDto>('/owner/alert-email-policy') });
  const tenants = useQuery({
    queryKey: ['tenants', 'for-alert-filter'],
    queryFn: () => get<{ data: TenantOption[] }>(`/platform/tenants${qs({ limit: 100 })}`),
    staleTime: 60_000,
  });

  useEffect(() => {
    if (overview.data) setPolicy(overview.data.policy);
  }, [overview.data]);

  const save = useMutation({
    mutationFn: () => put<OwnerAlertEmailOverviewDto>('/owner/alert-email-policy', { ...policy!, totpCode }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(QUERY_KEY, fresh);
      setTotpCode('');
      toast.success(t('alert_email_saved'));
    },
    onError: (error) => {
      setTotpCode('');
      toast.error(t('alert_email_save_failed'), describeError(error));
    },
  });

  if (overview.isError) return <Alert variant="warning">{describeError(overview.error)}</Alert>;
  if (!policy || !overview.data) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Spinner /> {t('alert_email_loading')}
      </div>
    );
  }

  const data = overview.data;
  const counts = new Map(data.counts.map((row) => [row.type, row]));
  const tenantNames = new Map<string, string>([
    ...(tenants.data?.data ?? []).map((tenant) => [tenant.id, `${tenant.slug} — ${tenant.name}`] as const),
    ...data.mutedTenants.map((tenant) => [tenant.tenantId, `${tenant.slug} — ${tenant.name}`] as const),
  ]);
  const routeLabel: Record<AlertEmailRoute, string> = {
    owner: t('alert_email_route_owner'),
    customer: t('alert_email_route_customer'),
    both: t('alert_email_route_both'),
    none: t('alert_email_route_none'),
  };
  const muteLabel: Record<MuteDays, string> = {
    '1': t('alert_email_mute_1_day'),
    '7': t('alert_email_mute_7_days'),
    '30': t('alert_email_mute_30_days'),
    forever: t('alert_email_mute_forever'),
  };
  const dedupeValid = policy.rules.every((rule) => Number.isInteger(rule.dedupeHours) && rule.dedupeHours >= 0 && rule.dedupeHours <= OWNER_ALERT_DEDUPE_MAX_HOURS);

  return (
    <div className="space-y-4">
      {!data.smtpConfigured && <Alert variant="warning">{t('alert_email_smtp_missing')}</Alert>}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('alert_email_title')}</CardTitle>
          <CardDescription>{t('alert_email_description', { days: data.sinceDays })}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Switch
            checked={policy.enabled}
            disabled={!canSave}
            onCheckedChange={(enabled) => setPolicy({ ...policy, enabled })}
            label={t('alert_email_enabled')}
            description={t('alert_email_enabled_hint')}
          />
          <p className="text-xs text-muted-foreground">
            {t('alert_email_owner_addresses', { list: data.ownerEmails.length > 0 ? data.ownerEmails.join(', ') : '—' })}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-xs text-muted-foreground">
                  <th className="py-2 pe-3 text-start font-medium">{t('alert_email_column_alert')}</th>
                  <th className="py-2 pe-3 text-start font-medium">{t('alert_email_column_send_to')}</th>
                  <th className="py-2 pe-3 text-start font-medium">{t('alert_email_column_repeat_hours')}</th>
                  <th className="py-2 pe-3 text-end font-medium">{t('alert_email_column_sent')}</th>
                  <th className="py-2 text-end font-medium">{t('alert_email_column_held')}</th>
                </tr>
              </thead>
              <tbody>
                {policy.rules.map((rule) => (
                  <tr key={rule.type} className="border-b border-border/60">
                    <td className="py-2 pe-3">{ALERT_TYPE_LABELS[rule.type]?.label ?? rule.type}</td>
                    <td className="py-2 pe-3">
                      <ChoiceButtons
                        layout="chips"
                        size="sm"
                        inline
                        aria-label={t('alert_email_column_send_to')}
                        disabled={!canSave || !policy.enabled}
                        value={rule.route}
                        onChange={(route) => setPolicy(setRule(policy, rule.type, { route }))}
                        options={ALERT_EMAIL_ROUTES.map((route) => ({ value: route, label: routeLabel[route] }))}
                      />
                    </td>
                    <td className="py-2 pe-3">
                      <Input
                        className="w-24"
                        inputMode="numeric"
                        aria-label={t('alert_email_column_repeat_hours')}
                        disabled={!canSave || !policy.enabled || rule.route === 'none'}
                        value={String(rule.dedupeHours)}
                        onChange={(e) => setPolicy(setRule(policy, rule.type, { dedupeHours: Number.parseInt(e.target.value.replace(/\D/g, '').slice(0, 3) || '0', 10) }))}
                      />
                    </td>
                    <td className="py-2 pe-3 text-end tabular-nums">{counts.get(rule.type)?.sent ?? 0}</td>
                    <td className="py-2 text-end tabular-nums">{counts.get(rule.type)?.heldBack ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">{t('alert_email_repeat_hint', { max: OWNER_ALERT_DEDUPE_MAX_HOURS })}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('alert_email_muted_title')}</CardTitle>
          <CardDescription>{t('alert_email_muted_description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {policy.mutes.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('alert_email_no_mutes')}</p>
          ) : (
            <ul className="space-y-1">
              {policy.mutes.map((mute) => (
                <li key={mute.tenantId} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1 truncate">{tenantNames.get(mute.tenantId) ?? mute.tenantId}</span>
                  <span className="text-xs text-muted-foreground">
                    {mute.until ? t('alert_email_muted_until', { when: dateTime(mute.until) }) : t('alert_email_mute_forever')}
                  </span>
                  {canSave && (
                    <Button size="xs" variant="ghost" aria-label={t('alert_email_unmute')} onClick={() => setPolicy(removeMute(policy, mute.tenantId))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canSave && (
            <div className="flex flex-wrap items-center gap-2">
              <Select className="w-64" aria-label={t('alert_email_facility')} value={muteTenant} onChange={(e) => setMuteTenant(e.target.value)}>
                <option value="">{t('alert_email_choose_facility')}</option>
                {(tenants.data?.data ?? []).map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.slug} — {tenant.name}
                  </option>
                ))}
              </Select>
              <ChoiceButtons
                inline
                aria-label={t('alert_email_mute_for')}
                value={muteDays}
                onChange={setMuteDays}
                options={MUTE_DAYS.map((days) => ({ value: days, label: muteLabel[days] }))}
              />
              <Button
                size="sm"
                variant="outline"
                disabled={!muteTenant}
                onClick={() => {
                  setPolicy(addMute(policy, muteTenant, muteDays, new Date()));
                  setMuteTenant('');
                }}
              >
                {t('alert_email_mute')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {canSave && (
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-72">
            <TotpField value={totpCode} onChange={setTotpCode} />
          </div>
          <Button loading={save.isPending} disabled={!dedupeValid || !isValidTotp(totpCode)} onClick={() => save.mutate()}>
            <Save className="h-4 w-4" /> {t('alert_email_save')}
          </Button>
        </div>
      )}
    </div>
  );
}
